import React from 'react';
import { motion } from 'framer-motion';
import { Heart, MapPin, Gift, Calendar } from 'lucide-react';

/**
 * Classic Template - Estilo elegante e tradicional
 * Paleta: off-white, bege, cinza claro
 * Tipografia: serifada
 * Animações: discretas (fade / reveal)
 */

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: "easeOut" }
};

const fadeIn = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true },
  transition: { duration: 1, ease: "easeOut" }
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: { staggerChildren: 0.1 }
  },
  viewport: { once: true }
};

const galleryItem = {
  initial: { opacity: 0, scale: 0.95 },
  whileInView: { opacity: 1, scale: 1 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

export function ClassicTemplate({ siteData, presentes, onPresentear, formatCurrency }) {
  const {
    couple_names = '',
    wedding_date = '',
    wedding_location = '',
    story = '',
    message = '',
    cover_image_url = '',
    gallery_images = []
  } = siteData || {};

  const formattedDate = wedding_date ? new Date(wedding_date).toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }) : '';

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
      {/* Hero Section */}
      <motion.section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
      >
        {/* Background */}
        {cover_image_url ? (
          <>
            <div className="absolute inset-0">
              <img 
                src={cover_image_url} 
                alt="Capa do casamento"
                className="w-full h-full object-cover object-center"
                style={{ objectPosition: 'center 30%' }}
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-stone-200 via-amber-50 to-stone-300" />
        )}
        
        {/* Content */}
        <motion.div 
          className="relative z-10 text-center px-4 max-w-3xl"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.6, delay: 0.5, type: "spring" }}
          >
            <Heart 
              className={`mx-auto mb-6 ${cover_image_url ? 'text-white' : 'text-stone-600'}`}
              size={48}
              strokeWidth={1.5}
            />
          </motion.div>
          
          <h1 className={`text-4xl md:text-6xl lg:text-7xl font-light tracking-wide mb-6 ${
            cover_image_url ? 'text-white drop-shadow-lg' : 'text-stone-800'
          }`}>
            {couple_names || 'Nosso Casamento'}
          </h1>
          
          {formattedDate && (
            <motion.p 
              className={`text-xl md:text-2xl tracking-widest uppercase mb-4 ${
                cover_image_url ? 'text-white/90' : 'text-stone-600'
              }`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <Calendar className="inline mr-3" size={20} strokeWidth={1.5} />
              {formattedDate}
            </motion.p>
          )}
          
          {wedding_location && (
            <motion.p 
              className={`text-lg md:text-xl flex items-center justify-center gap-2 ${
                cover_image_url ? 'text-white/80' : 'text-stone-500'
              }`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <MapPin size={18} strokeWidth={1.5} />
              {wedding_location}
            </motion.p>
          )}
        </motion.div>
        
        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className={`w-6 h-10 rounded-full border-2 ${
            cover_image_url ? 'border-white/50' : 'border-stone-400'
          } flex justify-center`}>
            <motion.div 
              className={`w-1.5 h-3 rounded-full mt-2 ${
                cover_image_url ? 'bg-white/70' : 'bg-stone-400'
              }`}
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </motion.section>

      {/* Message Section */}
      {message && (
        <motion.section 
          className="py-20 md:py-28 bg-stone-50"
          {...fadeIn}
        >
          <div className="max-w-3xl mx-auto px-6 text-center">
            <motion.div {...fadeInUp}>
              <div className="w-16 h-px bg-stone-300 mx-auto mb-8" />
              <blockquote className="text-xl md:text-2xl lg:text-3xl text-stone-700 font-light italic leading-relaxed">
                "{message}"
              </blockquote>
              <div className="w-16 h-px bg-stone-300 mx-auto mt-8" />
            </motion.div>
          </div>
        </motion.section>
      )}

      {/* Story Section */}
      {story && (
        <motion.section 
          className="py-20 md:py-28 bg-white"
          {...fadeIn}
        >
          <div className="max-w-3xl mx-auto px-6">
            <motion.div {...fadeInUp} className="text-center">
              <h2 className="text-3xl md:text-4xl font-light text-stone-800 mb-4">
                Nossa História
              </h2>
              <div className="w-20 h-px bg-amber-600/50 mx-auto mb-10" />
            </motion.div>
            <motion.p 
              {...fadeInUp}
              className="text-lg text-stone-600 leading-relaxed whitespace-pre-line text-center"
            >
              {story}
            </motion.p>
          </div>
        </motion.section>
      )}

      {/* Gallery Section */}
      {gallery_images.length > 0 && (
        <motion.section 
          className="py-20 md:py-28 bg-stone-100"
          {...fadeIn}
        >
          <div className="max-w-6xl mx-auto px-6">
            <motion.div {...fadeInUp} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-light text-stone-800 mb-4">
                Galeria
              </h2>
              <div className="w-20 h-px bg-amber-600/50 mx-auto" />
            </motion.div>
            
            <motion.div 
              className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6"
              {...staggerContainer}
            >
              {gallery_images.map((img, index) => (
                <motion.div
                  key={index}
                  {...galleryItem}
                  transition={{ delay: index * 0.1 }}
                  className="aspect-square overflow-hidden rounded-sm shadow-md hover:shadow-xl transition-shadow duration-300"
                >
                  <img 
                    src={img} 
                    alt={`Foto ${index + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.section>
      )}

      {/* Gift List Section */}
      {presentes && presentes.length > 0 && (
        <motion.section 
          className="py-20 md:py-28 bg-white"
          {...fadeIn}
        >
          <div className="max-w-5xl mx-auto px-6">
            <motion.div {...fadeInUp} className="text-center mb-12">
              <Gift className="mx-auto text-amber-600 mb-4" size={40} strokeWidth={1.5} />
              <h2 className="text-3xl md:text-4xl font-light text-stone-800 mb-4">
                Lista de Presentes
              </h2>
              <p className="text-stone-500">Sua presença é o nosso maior presente</p>
              <div className="w-20 h-px bg-amber-600/50 mx-auto mt-6" />
            </motion.div>
            
            <motion.div 
              className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
              {...staggerContainer}
            >
              {presentes.map((presente, index) => (
                <motion.div
                  key={presente.id}
                  {...galleryItem}
                  transition={{ delay: index * 0.1 }}
                  className="bg-stone-50 border border-stone-200 p-6 text-center hover:shadow-lg transition-shadow duration-300"
                >
                  <h3 className="text-xl text-stone-800 mb-2">{presente.titulo}</h3>
                  {presente.descricao && (
                    <p className="text-sm text-stone-500 mb-4">{presente.descricao}</p>
                  )}
                  <p className="text-2xl text-amber-700 font-light mb-6">
                    {formatCurrency(presente.valor)}
                  </p>
                  <button
                    onClick={() => onPresentear(presente)}
                    className="w-full py-3 px-6 bg-stone-800 text-white text-sm tracking-widest uppercase hover:bg-stone-700 transition-colors duration-300"
                    data-testid={`gift-btn-${presente.id}`}
                  >
                    Presentear
                  </button>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.section>
      )}

      {/* Footer */}
      <footer className="py-12 bg-stone-800 text-center">
        <p className="text-stone-400 text-sm tracking-wider">
          Feito com <Heart className="inline text-rose-400 mx-1" size={14} /> usando liventy
        </p>
      </footer>
    </div>
  );
}

export default ClassicTemplate;
